import React, { useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Drawer from '../../components/Drawer';
import ConfirmDialog from '../../components/ConfirmDialog';
import useEditRoute from '../../utils/useEditRoute';
import useTableList from '../../utils/useTableList';
import usePageTitle from '../../utils/usePageTitle';
import { movementTypesService } from '../../services/productionService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

function MovementTypesSettings() {
    usePageTitle('Tipos de movimento');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canManage = can('MovementTypes');

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        direction: 'in',
    });

    const list = useTableList({
        storageKey: 'movement-types',
        fetcher: (params) => movementTypesService.list(params),
        errorMessage: 'Não foi possível carregar tipos de movimento',
    });

    const filters = [
        { key: 'code', label: 'Código', type: 'text' },
        { key: 'name', label: 'Nome', type: 'text' },
        {
            key: 'direction',
            label: 'Tipo',
            type: 'select',
            options: [
                { value: 'in', label: 'Entrada' },
                { value: 'out', label: 'Saída' },
            ],
        },
    ];

    const columns = useMemo(
        () => [
            { key: 'code', label: 'Código', className: 'font-medium' },
            { key: 'name', label: 'Nome' },
            {
                key: 'direction',
                label: 'Tipo',
                render: (v) =>
                    v === 'in' ? (
                        <span className="text-emerald-700 font-medium">Entrada</span>
                    ) : (
                        <span className="text-rose-700 font-medium">Saída</span>
                    ),
            },
        ],
        [],
    );

    const openNew = () => {
        setEditing(null);
        setFormData({ code: '', name: '', direction: 'in' });
        setDrawerOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setFormData({
            code: row.code || '',
            name: row.name || '',
            direction: row.direction || 'in',
        });
        setDrawerOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            const payload = {
                code: formData.code.trim(),
                name: formData.name.trim(),
                direction: formData.direction,
            };
            if (editing?.id) {
                await movementTypesService.update(editing.id, payload);
            } else {
                await movementTypesService.create(payload);
            }
            toast({ title: 'Salvo', variant: 'success' });
            setDrawerOpen(false);
            list.reload();
        } catch (err) {
            console.error(err);
            toastApiError(err, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (row) => {
        setDeleteDialog({ open: true, id: row.id });
    };

    const handleDeleteConfirm = async () => {
        const id = deleteDialog.id;
        try {
            await movementTypesService.delete(id);
            setDeleteDialog({ open: false, id: null });
            if (editing?.id === id) {
                setDrawerOpen(false);
                setEditing(null);
            }
            list.reload();
            toast({
                title: 'Tipo excluído',
                description: 'O tipo de movimento foi excluído com sucesso',
                variant: 'success',
            });
        } catch (err) {
            console.error(err);
            toastApiError(err, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir este tipo' });
        }
    };

    useEditRoute({
        basePath: '/settings/movement-types',
        ready: list.loaded,
        drawerOpen: drawerOpen,
        open: id => {
            const row = list.rows.find((r) => r.id === id);
            if (row) openEdit(row);
        },
    });

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Tipos de movimento"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Configurações', path: '/settings/tenant' },
                    ]}
                    actions={
                        canManage
                            ? [
                                  {
                                      label: 'Novo tipo',
                                      icon: 'new',
                                      onClick: openNew,
                                  },
                              ]
                            : []
                    }
                />

                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por código ou nome..."
                    columns={columns}
                    emptyMessage="Nenhum tipo cadastrado"
                    editTo={canManage ? (row) => `/settings/movement-types/${row.id}` : undefined}
                    onEdit={canManage ? openEdit : undefined}
                    onDelete={canManage ? handleDeleteClick : undefined}
                />

                <ConfirmDialog
                    open={deleteDialog.open}
                    title="Excluir tipo de movimento"
                    message="Tem certeza que deseja excluir este tipo? Esta ação não pode ser desfeita."
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteDialog({ open: false, id: null })}
                />

                <Drawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    title={editing ? 'Editar tipo' : 'Novo tipo'}
                    size="md"
                    footer={
                        <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" form="movement-type-form" disabled={formLoading || !canManage}>
                                {formLoading ? 'Salvando…' : 'Salvar'}
                            </Button>
                        </div>
                    }
                >
                    <form id="movement-type-form" onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                            <Input
                                name="code"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                required
                                disabled={!!editing}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                            <Input
                                name="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Entrada / Saída *</label>
                            <select
                                value={formData.direction}
                                onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                                className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                            >
                                <option value="in">Entrada</option>
                                <option value="out">Saída</option>
                            </select>
                        </div>
                    </form>
                </Drawer>
            </div>
        </AppLayout>
    );
}

export default MovementTypesSettings;
